import React, { useEffect, useState, useRef } from 'react';
import HeroSection from '../components/HeroSection';
import TreatmentCaseSlider from '../components/TreatmentCaseSlider';
import { useLocation, useNavigate } from 'react-router-dom';

const FrontTeethPage = () => {
  // URL 파라미터에서 초기 탭 설정 (깜빡임 방지)
  const location = useLocation();
  const navigate = useNavigate();
  
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'resin-diastema'; // 기본값: 'resin-diastema'
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderHeight, setSliderHeight] = useState('auto');
  const heroRef = useRef(null);
  const slideRefs = useRef([]);

  // URL 변경 시 탭 상태 업데이트
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const newTab = tabParam || 'resin-diastema';
    setActiveTab(newTab);
  }, [location.search]);

  useEffect(() => {
    // fullPage.js 제거 (혹시 남아있을 경우)
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    // body에서 fullpage-enabled 클래스 제거
    document.body.classList.remove('fullpage-enabled');
    
    // 일반 스크롤 복원을 위한 스타일 추가
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // cleanup
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // activeTab이 변경될 때마다 페이지를 맨 위로 스크롤
  useEffect(() => {
    // DOM 업데이트 후 스크롤하도록 지연
    const timer = setTimeout(() => {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTab]);

  // 탭이 변경될 때 슬라이드 초기화
  useEffect(() => {
    setCurrentSlide(0);
  }, [activeTab]);

  // 슬라이드 높이 조정
  useEffect(() => {
    const updateSliderHeight = () => {
      const currentSlideElement = slideRefs.current[currentSlide];
      if (currentSlideElement) {
        const slideHeight = currentSlideElement.offsetHeight;
        setSliderHeight(`${slideHeight}px`);
      }
    };

    // DOM 업데이트 후 높이 계산
    const timer = setTimeout(updateSliderHeight, 100);
    
    // 윈도우 리사이즈 시에도 높이 재계산
    window.addEventListener('resize', updateSliderHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSliderHeight);
    };
  }, [currentSlide, activeTab]);

  const tabs = [
    { id: 'resin-diastema', name: '치간 이개부 레진치료', shortName: '레진' },
    { id: 'prosthetic-diastema', name: '치간 이개부 보철치료', shortName: '보철' },
    { id: 'caries-prosthetic', name: '충치 보철치료', shortName: '충치' },
    { id: 'whitening', name: '미백치료', shortName: '미백' },
  ];

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/front-teeth?tab=${tabId}`, { replace: true });
  };

  // 치간 이개부 레진치료 사례 데이터
  const resinDiastemaCases = [
    {
      id: '01',
      age: '50대',
      reason: '송곳니 사이가 벌어져서 떼우고 싶어요',
      treatment: '치간이개부 레진 충전 치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_04_치료전.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_04_치료후.webp'
    },
    {
      id: '02',
      age: '20대',
      reason: '앞니 사이가 벌어져서 떼우고 싶어요',
      treatment: '치간이개부 레진 충전 치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_02_치료전.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_02_치료후.webp'
    },
    {
      id: '03',
      age: '30대',
      reason: '앞니 사이가 벌어져서 떼우고 싶어요',
      treatment: '치간이개부 레진 충전 치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_03_치료전.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_03_치료후.webp'
    },
    {
      id: '04',
      age: '30대',
      reason: '아래 앞니 사이가 벌어져서 떼우고 싶어요',
      treatment: '치간이개부 레진 충전 치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_05_치료전.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_05_치료후.webp'
    },
    {
      id: '05',
      age: '30대',
      reason: '아래 앞니 사이가 벌어져 치료하고 싶어요',
      treatment: '치아 이개 레진충전 치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_01_치료전.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 레진치료/치간 이개부 레진치료_01_치료후.webp'
    }
  ];

  // 치간 이개부 보철치료 사례 데이터
  const prostheticDiastemaCases = [
    {
      id: '01',
      age: '40대',
      reason: '앞니 사이가 벌어지고 삐뚤어서 가지런하게 하고 싶어요',
      treatment: '크라운 보철치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_02_치료전.webp',
      duringImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_02_치료중.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_02_치료후.webp',
      overallDescription: '치료 중에는 임시치아를 정밀하게 제작하여 장착합니다. 임시치아를 잇몸 경계선에 정확히 맞게 세심하게 제작해야 최종 보철물이 잇몸선과 자연스럽게 조화를 이루며 아름답게 완성됩니다.'
    },
    {
      id: '02',
      age: '40대',
      reason: '앞니 사이가 벌어져서 메우고 싶어요',
      treatment: '크라운 보철치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_01_치료전.webp',
      duringImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_01_치료중.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_01_치료후.webp',
      overallDescription: '치료 중에는 임시치아를 정밀하게 제작하여 장착합니다. 임시치아를 잇몸 경계선에 정확히 맞게 세심하게 제작해야 최종 보철물이 잇몸선과 자연스럽게 조화를 이루며 아름답게 완성됩니다.'
    },
    {
      id: '03',
      age: '50대',
      reason: '아래 앞니 사이가 많이 벌어진 것을 해결하고 싶어요',
      treatment: '치간 이개부 보철치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_03_치료전.webp',
      duringImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_03_치료중.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_03_치료후.webp',
      overallDescription: '치료 중에는 임시치아를 정밀하게 제작하여 장착합니다. 임시치아를 잇몸 경계선에 정확히 맞게 세심하게 제작해야 최종 보철물이 잇몸선과 자연스럽게 조화를 이루며 아름답게 완성됩니다.'
    },
    {
      id: '04',
      age: '20대',
      reason: '앞니 사이가 벌어진 것을 해결하고 싶어요',
      treatment: '치간 이개부 보철치료',
      beforeImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_04_치료전.webp',
      duringImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_04_치료중.webp',
      afterImage: '/assets/앞니 자신감/치간 이개부 보철치료/치간 이개부 보철치료_04_치료후.webp',
      overallDescription: '치료 중에는 임시치아를 정밀하게 제작하여 장착합니다. 임시치아를 잇몸 경계선에 정확히 맞게 세심하게 제작해야 최종 보철물이 잇몸선과 자연스럽게 조화를 이루며 아름답게 완성됩니다.'
    }
  ];

  // 충치 보철치료 사례 데이터
  const cariesProstheticCases = [
    {
      id: '01',
      age: '30대',
      reason: '앞니가 썩고 부러졌어요',
      treatment: '신경치료 + 크라운 보철치료',
      beforeImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_01_치료전.webp',
      duringImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_01_치료중.webp',
      afterImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_01_치료후.webp'
    },
    {
      id: '02',
      age: '20대',
      reason: '앞니가 썩고 부러졌어요',
      treatment: '신경치료 + 크라운 보철치료',
      beforeImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_02_치료전.webp',
      duringImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_02_치료중.webp',
      afterImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_02_치료후.webp'
    },
    {
      id: '03',
      age: '50대',
      reason: '아래 앞니가 많이 썩었는데 가능한 치아를 살리고 싶어요',
      treatment: '신경치료 후 브릿지 치료',
      beforeImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_03_치료전.webp',
      duringImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_03_치료중.webp',
      afterImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_03_치료후.webp'
    },
    {
      id: '04',
      age: '40대',
      reason: '이가 너무 많이 상했어요',
      treatment: '전악 치석제거 및 잇몸치료 + 전치부 레진충전, 크라운 및 임플란트 치료',
      beforeImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_04_치료전.webp',
      afterImage: '/assets/앞니 자신감/충치 보철치료/충치 보철치료_04_치료후.webp'
    }
  ];

  // 미백치료 사례 데이터
  const whiteningCases = [
    {
      id: '01',
      age: '30대',
      reason: '치아를 하얗게 하고 싶어요',
      treatment: '전문가 미백 치료 (3회)',
      beforeImage: '/assets/앞니 자신감/미백치료/미백치료_02_치료전.webp',
      afterImage: '/assets/앞니 자신감/미백치료/미백치료_02_치료후.webp'
    },
    {
      id: '02',
      age: '20대',
      reason: '치아를 더 하얗게 하고 싶어요',
      treatment: '전문가 미백 치료 (3회)',
      beforeImage: '/assets/앞니 자신감/미백치료/미백치료_01_치료전.webp',
      afterImage: '/assets/앞니 자신감/미백치료/미백치료_01_치료후.webp'
    }
  ];

  const renderResinDiastemaContent = () => (
    <div className="space-y-16">
      {/* 치간 이개부 레진치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">치간 이개부 레진치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">앞니 사이 틈 때문에 웃을 때마다 신경 쓰이시나요?</p>
          <p className="mb-4">
            <strong className="text-primary">"웃을 때 틈이 보여서 입을 가리게 돼요"</strong><br />
            이런 고민, 이제 그만하세요!
          </p>
          <p className="mb-4">
            앞니 사이의 공간을 치아색과 동일한 레진으로 자연스럽게 메워서<br />
            <strong className="text-secondary">단 하루 만에 자신감 넘치는 미소를 완성</strong>할 수 있습니다.
          </p>
          <p className="text-lg">
            치아를 전혀 깎지 않고도 가능한 보존적 치료로<br />
            <strong className="text-primary">소중한 자연치아를 그대로 보존하면서 완벽한 심미성을 얻을 수 있습니다.</strong>
          </p>
        </div>

        {/* 치간 이개부 레진치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-rose-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">즉시 완성</h4>
            <p className="text-gray-600 text-sm">오늘 치료하고 오늘 바로 웃으세요</p>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">보존적 치료</h4>
            <p className="text-gray-600 text-sm">치아를 가능한 삭제하지 않고 보존적 치료로 진행합니다</p>
          </div>
          
          <div className="bg-emerald-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">완벽한 색상 매칭</h4>
            <p className="text-gray-600 text-sm">내 치아와 구분할 수 없을 정도로 자연스럽게</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 레진치료 */}
        <div className="bg-gradient-to-r from-rose-50 via-pink-50 to-purple-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 레진치료</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"단순히 메우기만 하는 치료가 아닙니다"</strong>
            </p>
            <p className="text-lg text-gray-700">
              치아의 형태, 색상, 투명도까지 세심하게 고려하여<br />
              <strong className="text-primary">마치 원래 그랬던 것처럼 자연스러운 결과</strong>를 만들어드립니다.
            </p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-rose-50 via-pink-50 to-purple-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">치간 이개부 레진치료 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 분석</h4>
            <p className="text-sm text-gray-600">틈의 크기, 모양, 원인 정확히 파악</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">완벽한 색상 매칭</h4>
            <p className="text-sm text-gray-600">내 치아와 구분 불가능한 색상 선택</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정교한 레진 성형</h4>
            <p className="text-sm text-gray-600">자연스러운 치아 형태로 정밀 성형</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">완벽한 마무리</h4>
            <p className="text-sm text-gray-600">자연치아와 동일한 광택과 질감</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={resinDiastemaCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          title="치료 사례"
          gradientColors="from-pink-50 to-purple-50"
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">치간 이개부 레진치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          앞니 사이 틈 때문에 웃음이 어색하신가요?<br />
          하루 만에 완성되는 자연스러운 레진치료로 자신감 있는 미소를 만들어보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderProstheticDiastemaContent = () => (
    <div className="space-y-16">
      {/* 치간 이개부 보철치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">치간 이개부 보철치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">단순한 틈 메우기가 아닌, 완전히 새로운 앞니를 원하시나요?</p>
          <p className="mb-4">
            <strong className="text-primary">"틈도 메우고 모양도 예쁘게, 색깔도 하얗게 만들고 싶어요"</strong><br />
            이 모든 것이 한 번에 가능합니다!
          </p>
          <p className="mb-4">
            라미네이트나 크라운으로 치아의 모양, 크기, 색상을 완전히 바꿔서<br />
            <strong className="text-secondary">드라마 속 주인공 같은 완벽한 앞니</strong>를 만들어드립니다.
          </p>
          <p className="text-lg">
            단순히 치료하는 것이 아니라 <strong className="text-primary">당신만의 이상적인 미소를 디자인</strong>하여<br />
            평생 자신감 있게 웃을 수 있도록 도와드립니다.
          </p>
        </div>

        {/* 치간 이개부 보철치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-violet-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">완벽한 변신</h4>
            <p className="text-gray-600 text-sm">모양, 크기, 색상 모든 것을 이상적으로</p>
          </div>
          
          <div className="bg-sky-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">반영구적 지속</h4>
            <p className="text-gray-600 text-sm">10년 이상 아름다움이 지속됩니다</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">개인 맞춤 디자인</h4>
            <p className="text-gray-600 text-sm">얼굴형과 성격에 맞는 완벽한 설계</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 보철치료 */}
        <div className="bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 보철치료</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"단순히 치료하는 것이 아니라 미소를 디자인합니다"</strong>
            </p>
            <p className="text-lg text-gray-700">
              얼굴형, 입술 라인, 개인의 성격까지 고려하여<br />
              <strong className="text-primary">당신에게 가장 어울리는 완벽한 앞니</strong>를 만들어드립니다.
            </p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-indigo-50 to-cyan-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">치간 이개부 보철치료 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 진단</h4>
            <p className="text-sm text-gray-600">치아 상태와 얼굴형 분석</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">맞춤 설계</h4>
            <p className="text-sm text-gray-600">이상적인 앞니 모양 설계</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">치아 준비</h4>
            <p className="text-sm text-gray-600">최소한의 치아 삭제</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">임시치아</h4>
            <p className="text-sm text-gray-600">제작 기간 중 임시치아 장착</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 장착</h4>
            <p className="text-sm text-gray-600">완성된 보철물 정밀 장착</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={prostheticDiastemaCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          title="치료 사례"
          gradientColors="from-indigo-50 to-cyan-50"
          showDuringImage={true}
          showOverallDescription={true}
          description="치료 중에는 임시치아를 정밀하게 제작하여 장착합니다. 임시치아를 잇몸 경계선에 정확히 맞게 세심하게 제작해야 최종 보철물이 잇몸선과 자연스럽게 조화를 이루며 아름답게 완성됩니다."
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">치간 이개부 보철치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          단순한 틈 메우기를 넘어 완벽한 앞니로 바꾸고 싶으신가요?<br />
          개인 맞춤 보철치료로 이상적인 미소를 만들어보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderCariesProstheticContent = () => (
    <div className="space-y-16">
      {/* 충치 보철치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">충치 보철치료</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">앞니 충치 때문에 웃을 때 신경 쓰이시나요?</p>
          <p className="mb-4">
            <strong className="text-primary">충치 치료와 동시에 아름다운 앞니를 만들어드립니다.</strong>
          </p>
          <p className="mb-4">
            앞니에 생긴 충치를 치료하면서 크라운이나 라미네이트로<br />
            <strong className="text-secondary">기능과 심미성을 동시에 회복</strong>시켜드립니다.
          </p>
          <p className="text-lg">
            충치로 손상된 부분을 완전히 제거하고<br />
            <strong className="text-primary">자연치아보다 더 아름다운 앞니로 만들어드립니다.</strong>
          </p>
        </div>

        {/* 충치 보철치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-orange-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">완벽한 충치 제거</h4>
            <p className="text-gray-600 text-sm">재발 방지를 위한 철저한 치료</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">기능과 심미 동시 회복</h4>
            <p className="text-gray-600 text-sm">씹기 기능과 미관 모두 개선</p>
          </div>
          
          <div className="bg-rose-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">장기적 안정성</h4>
            <p className="text-gray-600 text-sm">튼튼하고 오래가는 치료 결과</p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-orange-50 to-rose-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">충치 보철치료 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 진단</h4>
            <p className="text-sm text-gray-600">충치 범위와 치아 상태 확인</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">충치 제거</h4>
            <p className="text-sm text-gray-600">손상된 부분 완전 제거</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">신경치료</h4>
            <p className="text-sm text-gray-600">필요시 신경치료 진행</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">보철 제작</h4>
            <p className="text-sm text-gray-600">맞춤형 크라운 제작</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 장착</h4>
            <p className="text-sm text-gray-600">정밀한 보철물 장착</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={cariesProstheticCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          title="치료 사례"
          gradientColors="from-orange-50 to-rose-50"
          showDuringImage={true}
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">충치 보철치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          앞니 충치 때문에 웃음을 가리고 계신가요?<br />
          충치 치료와 동시에 아름다운 앞니로 만들어보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderWhiteningContent = () => (
    <div className="space-y-16">
      {/* 미백치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">미백치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">누런 치아 때문에 웃을 때 자신감이 없으신가요?</p>
          <p className="mb-4">
            <strong className="text-primary">"하얀 치아로 환하게 웃고 싶어요!"</strong><br />
            이제 그 꿈을 현실로 만들어드립니다.
          </p>
          <p className="mb-4">
            커피, 차, 담배 등으로 누렇게 변색된 치아를<br />
            <strong className="text-secondary">안전하고 효과적인 전문가 미백치료</strong>로 하얗고 밝게 만들어드립니다.
          </p>
          <p className="text-lg">
            치아를 전혀 깎지 않고도 가능한 <strong className="text-primary">가장 보존적인 심미치료</strong>로<br />
            자연스럽고 건강한 하얀 미소를 되찾으세요.
          </p>
        </div>

        {/* 미백치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-cyan-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">즉시 효과</h4>
            <p className="text-gray-600 text-sm">단 한 번의 시술로 바로 확인되는 미백 효과</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">안전한 시술</h4>
            <p className="text-gray-600 text-sm">FDA 승인 미백제로 안전하게</p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">무삭제 치료</h4>
            <p className="text-gray-600 text-sm">건강한 치아를 전혀 깎지 않습니다</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 미백치료 */}
        <div className="bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 미백치료</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"단순히 하얗게만 하는 것이 아닙니다"</strong>
            </p>
            <p className="text-lg text-gray-700">
              개인의 치아 상태와 원하는 미백 정도를 정확히 파악하여<br />
              <strong className="text-primary">자연스럽고 건강한 하얀 미소</strong>를 만들어드립니다.
            </p>
          </div>
        </div>
      </div>



      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">전문가 미백치료 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">상담 및 검사</h4>
            <p className="text-sm text-gray-600">치아 상태 확인 및 미백 가능 여부 진단</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">잇몸 보호</h4>
            <p className="text-sm text-gray-600">잇몸을 보호하는 젤 도포</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">미백제 도포</h4>
            <p className="text-sm text-gray-600">전문가용 고농도 미백제 적용</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">완료 및 관리</h4>
            <p className="text-sm text-gray-600">미백치료 후 치아시림을 방지하기 위해 불소 도포</p>
          </div>
        </div>
      </div>

      {/* 미백 후 주의사항 */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">미백 후 주의사항</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              피해야 할 것들
            </h4>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                커피, 차, 와인 등 색소가 강한 음료
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                카레, 토마토소스 등 색소가 강한 음식
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                담배, 흡연
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                색소가 강한 구강청결제
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              권장사항
            </h4>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                물, 우유 등 무색 음료 섭취
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                하얀 음식 위주로 식단 구성
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                정기적인 치아 관리
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                미백 전용 치약 사용
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={whiteningCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          title="치료 사례"
          gradientColors="from-cyan-50 to-blue-50"
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">미백치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          누런 치아 때문에 웃음을 가리고 계신가요?<br />
          안전하고 효과적인 전문가 미백치료로 환한 미소를 되찾아보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );



  const renderTabContent = () => {
    switch (activeTab) {
      case 'resin-diastema':
        return renderResinDiastemaContent();
      case 'prosthetic-diastema':
        return renderProstheticDiastemaContent();
      case 'caries-prosthetic':
        return renderCariesProstheticContent();
      case 'whitening':
        return renderWhiteningContent();
      default:
        return renderResinDiastemaContent();
    }
  };

  return (
    <div className="front-teeth-page-container">
      {/* 히어로 섹션 */}
      <HeroSection 
        title="앞니 자신감"
        subtitle="완벽한 미소로 당당한 자신감을 되찾으세요"
        backgroundImage="/assets/앞니자신감배경.png"
        brandText="한앤손 부부치과"
        ref={heroRef}
      >
        {/* 탭 네비게이션 */}
        <div className="flex justify-center">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-1 inline-flex max-w-full border border-white/30">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-2.5 md:px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-0 flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-white text-primary shadow-md'
                      : 'text-white/90 hover:text-white hover:bg-white/20'
                  }`}
                >
                  <span className="block md:hidden">{tab.shortName}</span>
                  <span className="hidden md:block">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </HeroSection>

      {/* 콘텐츠 영역 */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="transition-all duration-300">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default FrontTeethPage; 